// 動画作成/編集フォームの保存場所(フォルダツリー選択)の状態管理
//
// ダッシュボードの共通データ供給要素 `#dashboard-data-source` (useDataSource.js) を
// 再利用せず、専用の `#video-form-folder-data-source` を独自に読み取る設計にしている。
// `#dashboard-data-source` を使うと useDataSource() が返す共有の `folders` ref が
// このページでも非空になり、app.js の ensureDefaultFolder()/restoreSelection() が
// `selectedFolderId` を書き換えてしまう副作用がある(本ツリーは自身の `pickerFolderId` で
// 選択状態を管理しており、この上書きにより意図しないフォルダが選択された状態になる)。
// syncFolderIdFields() は検索フォーム(#mobile-search-form/#simple-search-form)限定に
// スコープ修正済みで本ツリーの hidden input には影響しないが、上記の副作用を避けるため
// データ供給元自体は引き続き分離している。
import { nextTick, computed, ref } from 'vue';
import { useFolderTree } from './useFolderTree';

const parseJsonAttribute = (element, attribute, fallback = null) => {
  if (!element || !element.hasAttribute(attribute)) {
    return fallback;
  }
  const raw = element.getAttribute(attribute);
  if (raw === null || raw === '') {
    return fallback;
  }
  try {
    return JSON.parse(raw);
  } catch (error) {
    return fallback;
  }
};

export const useVideoFormFolderPicker = () => {
  const folders = ref([]);
  const allowedFolderIds = ref(null);
  // '' (未選択) | フォルダid(数値) | 'root' (TOP・クライアント内部の sentinel)
  const pickerFolderId = ref('');

  const {
    topLevelFolders,
    getChildren,
    isCollapsed,
    toggleFolder,
    expandAncestorsOf,
  } = useFolderTree(folders, pickerFolderId, {
    persist: false,
    initialCollapse: 'collapseAll',
    scrollContainerSelector: '.folder-picker-tree',
  });

  const selectFolder = (folderId) => {
    const parsed = Number(folderId);
    if (!Number.isFinite(parsed)) {
      return;
    }
    pickerFolderId.value = parsed;
  };

  const selectRoot = () => {
    pickerFolderId.value = 'root';
  };

  // 'root' 選択中かどうか。hidden input `folder_id`/`to_root` の disabled 出し分けにも使う
  // (folder_id は required_without:to_root のため、root 選択時は folder_id を disabled にして
  // 送信対象から外し、代わりに to_root を有効化する)。
  const isRootSelected = computed(() => pickerFolderId.value === 'root');

  // hidden input name="folder_id" へセットする値。数値idのみを返し、未選択('')・TOP('root')は空文字
  const inputValue = computed(() => {
    const v = pickerFolderId.value;
    return typeof v === 'number' ? String(v) : '';
  });

  const init = async () => {
    const el = document.getElementById('video-form-folder-data-source');
    if (!el) {
      // ダッシュボード等、このデータ供給要素が無いページでは no-op
      return;
    }

    const parsedFolders = parseJsonAttribute(el, 'data-folders', []);
    folders.value = Array.isArray(parsedFolders) ? parsedFolders : [];
    allowedFolderIds.value = parseJsonAttribute(el, 'data-allowed-folder-ids');

    const rawFolderId = parseJsonAttribute(el, 'data-video-form-folder-id');

    if (rawFolderId === 'root') {
      pickerFolderId.value = 'root';
      return;
    }

    const parsed = Number(rawFolderId);
    if (rawFolderId === null || !Number.isFinite(parsed) || parsed <= 0) {
      return;
    }
    pickerFolderId.value = parsed;

    // folders 更新をトリガーに useFolderTree 側の初期折りたたみ(collapseAll)計算が
    // 非同期(pre flush)で走るため、それが確定してから祖先展開しないと上書きされる
    await nextTick();
    expandAncestorsOf(parsed);
    // 展開による子ノード(FolderNode.vue の v-if="children.length && !collapsed")のDOM描画確定を待つ
    await nextTick();
    // ページ全体を巻き込む scrollIntoView は使わず、ツリーボックス内部のみスクロールする
    const container = document.querySelector('.folder-picker-tree');
    const target = container?.querySelector(`.folder-link[data-folder-id="${parsed}"]`);
    if (container && target) {
      container.scrollTop += target.getBoundingClientRect().top - container.getBoundingClientRect().top;
    }
  };

  return {
    folders,
    allowedFolderIds,
    pickerFolderId,
    topLevelFolders,
    getChildren,
    isCollapsed,
    toggleFolder,
    selectFolder,
    selectRoot,
    isRootSelected,
    inputValue,
    init,
  };
};
